import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import {
	ADJUSTMENT_TYPE_INITIAL_VALUES,
	CatalogAdjustmentType,
} from "@/pages/configuration/models/CatalogAdjustmentType";
import { UnitSelector } from "@/shared/selectors/UnitSelector";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	deleteAdjustmentType,
	upsertAdjustmentType,
} from "../../../repositories/CatalogAdjustmentTypeRepository";

type Props = {
	adjustmentType: CatalogAdjustmentType | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (id?: number) => void;
};

export const UpsertDeleteModal = ({ adjustmentType, modal, onHide }: Props) => {
	const [title, setTitle] = useState<string>("CREATE");

	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const { control, reset, handleSubmit, watch, setValue, getValues } =
		useForm<CatalogAdjustmentType>({
			defaultValues: ADJUSTMENT_TYPE_INITIAL_VALUES,
		});

	const requiredValue = watch("RequiredValue");

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteAdjustmentType, {
		meta: { successMessage: "The adjustment type was deleted successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["adjustmentTypes"] });
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});
	const upsertMutation = useMutation(upsertAdjustmentType, {
		meta: {
			successMessage: `The adjustment type was ${
				modal === "create" ? "created" : "updated"
			} successfully`,
		},
		onSuccess: async (id: number) => {
			await queryClient.invalidateQueries({ queryKey: ["adjustmentTypes"] });
			onHide(id);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<CatalogAdjustmentType> = async (
		data,
		event,
	) => {
		setIsSubmitLoading(true);
		if (modal === "delete") {
			deleteMutation.mutate(data.AdjustmentTypeId);
		} else {
			upsertMutation.mutate(data);
		}
	};

	useEffect(() => {
		if (modal !== "") {
			setIsSubmitLoading(false);
			reset(adjustmentType || ADJUSTMENT_TYPE_INITIAL_VALUES);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
		}
	}, [modal, adjustmentType]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						adjustmentType === null
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Adjustment Type - Adjustment Types`}
				/>
			)}
			<MesfModal
				title={`${title} ADJUSTMENT TYPE`}
				open={modal !== ""}
				handleClose={() => onHide()}
				maxWidth="sm"
				id="adjustment-type-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="AdjustmentTypeName"
									control={control}
									rules={{ required: "Name is required" }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											required
											label="Name"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={modal === "delete"}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
								<Controller
									name="RequiredValue"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													onChange={(e, checked) => {
														field.onChange(checked);
														setValue("Unit", null);
													}}
													name="RequiredValue"
													color="primary"
													disabled={modal === "delete"}
												/>
											}
											label="Required Value"
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
								<Controller
									name="RequiredReason"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													onChange={field.onChange}
													name="RequiredReason"
													color="primary"
													disabled={modal === "delete"}
												/>
											}
											label="Required Reason"
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
								<Controller
									name="Unit"
									rules={{
										required: requiredValue ? "The unit is required" : false,
									}}
									control={control}
									render={({ field, fieldState: { error } }) => (
										<UnitSelector
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											disabled={!requiredValue || modal === "delete"}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide()}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									onClick={() => onSubmit(getValues())}
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In AdjustmentType"
			/>
		</>
	);
};
